"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyProjection = void 0;
const projection_zod_1 = require("./projection-zod");
const projection_apply_1 = require("./projection-apply");
const COLUMN_RE = /^[a-z_][a-z0-9_]*$/;
const applyProjection = (proj, opts) => {
    // Пустая проекция (null / [] / undefined) = «маски нет» — defensive-паритет
    // с нормализацией в контракте (crudProjectionField), покрывает прямой вызов.
    const fields = (0, projection_zod_1.isEmptyProjection)(proj.fields) ? undefined : proj.fields;
    const fieldsOmit = (0, projection_zod_1.isEmptyProjection)(proj.fieldsOmit) ? undefined : proj.fieldsOmit;
    if (fields === undefined && fieldsOmit === undefined)
        return null;
    if (fields !== undefined && fieldsOmit !== undefined) {
        throw new Error('applyProjection: fields and fieldsOmit are mutually exclusive');
    }
    const params = [];
    const paramOffset = opts.paramOffset ?? 0;
    const addParam = (value) => {
        params.push(value);
        return `$${paramOffset + params.length}`;
    };
    const mapField = (field) => {
        if (!opts.allowedFields.includes(field)) {
            throw new Error(`applyProjection: field '${field}' is not in allowedFields whitelist (zod should have rejected earlier)`);
        }
        const col = opts.fieldMapping?.[field] ?? field;
        if (!COLUMN_RE.test(col)) {
            throw new Error(`applyProjection: unsafe column '${col}' for field '${field}'`);
        }
        return col;
    };
    const parse = (raw) => {
        const { head, segments } = (0, projection_zod_1.splitProjectionPath)(raw);
        if (!opts.allowedFields.includes(head)) {
            throw new Error(`applyProjection: field '${head}' is not in allowedFields whitelist (zod should have rejected earlier)`);
        }
        for (const seg of [head, ...segments]) {
            if (!projection_zod_1.CRUD_PROJECTION_SEGMENT_RE.test(seg)) {
                throw new Error(`applyProjection: invalid segment in '${raw}'`);
            }
            if (projection_zod_1.DANGEROUS_PROJECTION_SEGMENTS.has(seg)) {
                throw new Error(`applyProjection: reserved prototype key in '${raw}'`);
            }
        }
        if (segments.length > 0 && !opts.jsonbFields.includes(head)) {
            throw new Error(`applyProjection: '${raw}' — field '${head}' is not a jsonb field`);
        }
        return { head, segments };
    };
    if (fields !== undefined) {
        const wholeHeads = new Set(projection_apply_1.CRUD_PROJECTION_FORCED_KEYS);
        const pathsByHead = new Map();
        for (const raw of fields) {
            const { head, segments } = parse(raw);
            if (segments.length === 0) {
                wholeHeads.add(head);
            }
            else {
                const list = pathsByHead.get(head) ?? [];
                list.push(segments);
                pathsByHead.set(head, list);
            }
        }
        const selectParts = [];
        const plainFields = [];
        const pathAliases = [];
        // Порядок стабильный (не по прихоти клиента): id/tenantId первыми,
        // дальше — порядок allowedFields (у snapshot domain-ключи идут до
        // платформенных, форс-ключи вытаскиваем вперёд явно).
        const orderedFields = [
            ...projection_apply_1.CRUD_PROJECTION_FORCED_KEYS,
            ...opts.allowedFields.filter((field) => !projection_apply_1.CRUD_PROJECTION_FORCED_KEYS.includes(field)),
        ];
        for (const field of orderedFields) {
            if (wholeHeads.has(field)) {
                selectParts.push(mapField(field));
                plainFields.push(field);
            }
            else if (pathsByHead.has(field)) {
                const col = mapField(field);
                (0, projection_zod_1.pruneSubsumedPaths)(pathsByHead.get(field)).forEach((segments, i) => {
                    const alias = `${col}__${i}`;
                    selectParts.push(`(${col} #> ${addParam(segments)}::text[]) AS "${alias}"`);
                    pathAliases.push({ field, alias, segments });
                });
            }
        }
        return { select: selectParts.join(', '), params, plainFields, pathAliases };
    }
    // fieldsOmit
    const omitWhole = new Set();
    const omitPathsByHead = new Map();
    for (const raw of fieldsOmit) {
        const { head, segments } = parse(raw);
        if (segments.length === 0) {
            if (projection_apply_1.CRUD_PROJECTION_FORCED_KEYS.includes(head))
                continue; // O5
            // молчаливый whitelist-check даже для omit (унификация ошибок)
            mapField(head);
            omitWhole.add(head);
        }
        else {
            const list = omitPathsByHead.get(head) ?? [];
            list.push(segments);
            omitPathsByHead.set(head, list);
        }
    }
    const selectParts = [];
    const plainFields = [];
    for (const field of opts.allowedFields) {
        if (omitWhole.has(field))
            continue;
        const col = mapField(field);
        const omitPaths = omitPathsByHead.get(field);
        if (omitPaths !== undefined) {
            const chain = (0, projection_zod_1.pruneSubsumedPaths)(omitPaths)
                .map((segments) => ` #- ${addParam(segments)}::text[]`)
                .join('');
            selectParts.push(`(${col}${chain}) AS "${col}"`);
        }
        else {
            selectParts.push(col);
        }
        plainFields.push(field);
    }
    return { select: selectParts.join(', '), params, plainFields, pathAliases: [] };
};
exports.applyProjection = applyProjection;
//# sourceMappingURL=projection-sql.js.map